import clsx from 'clsx'
import { Minus, Plus } from 'lucide-react'
import React, { useCallback } from 'react'

import Text, { textVariants } from '@/components-latest/primitives/Text'

import Styles from './Counter.module.scss'

interface Props {
  onChange: (value: number) => void
  minNumber?: number
  value: number
  className?: string
  disabled?: boolean
  caption?: string
  step?: number
  maxNumber?: number
}

const Counter = React.memo(
  ({
    onChange,
    className,
    value,
    minNumber = 0,
    disabled,
    caption,
    step = 1,
    maxNumber = Number.MAX_VALUE
  }: Props) => {
    const onCounterInputChange = useCallback(
      (value: string) => {
        if (!value) {
          onChange(minNumber || 0)
        }
        const isValueValid = /^-?\d+$/.test(value)
        const _value = Number(value)

        let adjustedValue = _value
        if (_value < minNumber) adjustedValue = minNumber
        if (_value > maxNumber) adjustedValue = maxNumber

        if (isValueValid) {
          onChange(Number(adjustedValue))
        }
      },
      [minNumber, onChange, maxNumber]
    )

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value
      onCounterInputChange(value)
    }

    const onIncreaseNumber = () => {
      onCounterInputChange(String(value + step))
    }
    const onDecreaseNumber = () => {
      onCounterInputChange(String(value - step))
    }

    return (
      <div className={clsx(Styles.counter, className)}>
        <button
          data-testid="decrease-counter-button"
          disabled={value === minNumber || disabled}
          onClick={onDecreaseNumber}
          className={clsx(Styles.iconWrapper)}>
          <Minus />
        </button>
        <div className={Styles.numberContent}>
          <input
            data-testid="counter-input"
            onChange={onInputChange}
            value={value}
            type="text"
            className={clsx(
              Styles.input,
              textVariants({ variant: 'subtitle', weight: 'extraBold' })
            )}
            disabled
          />
          {caption && (
            <Text asChild>
              <p>{caption}</p>
            </Text>
          )}
        </div>
        <button
          data-testid="increase-counter-button"
          disabled={value === maxNumber || disabled}
          onClick={onIncreaseNumber}
          className={clsx(Styles.iconWrapper)}>
          <Plus />
        </button>
      </div>
    )
  }
)

export default Counter
